import { useFetchMessages } from '@faceup/localization'
import { type Language, languageEnumToBasename, RTL_LANGUAGES } from '@faceup/utils'
import moment from 'moment-timezone'
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { FullScreenLoader } from '../Components/FullScreenLoader'
import { translations } from '../locales'
import { LanguageContext, type TextDirection } from './LanguageContext'
import { setRequestLanguage } from './requestLanguage'

const getDirection = (language: Language): TextDirection =>
  RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr'

const setHtmlAttributes = (language: Language) => {
  const basename = languageEnumToBasename(language)
  document.documentElement.setAttribute('lang', basename)
  document.documentElement.setAttribute('dir', getDirection(language))
}

type AntLocale = Awaited<ReturnType<(typeof translations.ant)[Language]>>['default']

type LoadedBundle = {
  language: Language
  messages: Record<string, string>
  antLocale: AntLocale
}

type LanguageProviderProps = {
  children: ReactNode
  language: Language
}

/**
 * Controlled, swap-in-place. The language is decided above (URL param resolved
 * by the route wrapper) and passed in. The previous bundle stays on screen until
 * the new one is ready, then the context value swaps atomically - consumers
 * re-render, nothing unmounts.
 */
export const LanguageProvider = ({ children, language }: LanguageProviderProps) => {
  const [newMessages, setNewMessages] = useState<Pick<
    LoadedBundle,
    'language' | 'messages'
  > | null>(null)
  const [loaded, setLoaded] = useState<LoadedBundle | null>(null)

  const handleNewMessages = useCallback((messages: Record<string, string>, language: Language) => {
    setNewMessages({ language, messages })
  }, [])

  const { getMessages, forceRefetch } = useFetchMessages({ handleNewMessages })

  // Load the requested language in the background; a newer language change
  // cancels an in-flight load so it can't overwrite a fresher bundle.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const [antLocale, cdnMessages] = await Promise.all([
        translations.ant[language]().then(m => m.default),
        getMessages(language),
      ])
      if (cancelled) {
        return
      }
      setLoaded({
        language,
        messages: cdnMessages,
        antLocale,
      })
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [language, getMessages])

  // Re-pull CDN translations for the current language without dropping state.
  useEffect(() => {
    const onRefresh = () => {
      void forceRefetch(language)
    }
    window.addEventListener('focalazy:refresh-translations', onRefresh)
    return () => window.removeEventListener('focalazy:refresh-translations', onRefresh)
  }, [language, forceRefetch])

  // Reflect the loaded language on <html>, moment and the GraphQL request header
  // (so there's no mid-flight mismatch between UI text and outgoing requests).
  useEffect(() => {
    if (!loaded) {
      return
    }
    setHtmlAttributes(loaded.language)
    moment.locale(languageEnumToBasename(loaded.language))
    setRequestLanguage(loaded.language)
  }, [loaded])

  // First paint only: nothing loaded yet. Every later change keeps the previous
  // bundle on screen until the new one arrives, so no unmount.
  if (!loaded) {
    return <FullScreenLoader />
  }

  return (
    <LanguageContext.Provider
      value={{
        language: loaded.language,
        messages:
          newMessages?.language === loaded.language
            ? { ...loaded.messages, ...newMessages.messages }
            : loaded.messages,
        antLocale: loaded.antLocale,
        direction: getDirection(loaded.language),
      }}
    >
      {children}
    </LanguageContext.Provider>
  )
}
